/**
 * Bridge Club Bulk-Order Visibility Curation
 *
 * Fast admin page for toggling which feed formulas appear in the Bridge
 * Club bulk-order catalog (FeedFormula.available_bridge_club_bulk).
 * Saves each toggle immediately (debounced) so there is no "Save All".
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface Formula {
  id: number;
  name: string;
  code: string;
  feed_grade: string;
  feed_grade_display: string;
  animal_type: string;
  animal_type_display: string;
  is_custom_formula: boolean;
  available_bridge_club_bulk: boolean;
  has_bulk_pricing: boolean;
}

type GradeFilter = 'ALL' | 'FIVE_STAR' | 'ESSENTIAL' | 'PLUS';

const GRADE_FILTERS: { value: GradeFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'FIVE_STAR', label: 'Five Star' },
  { value: 'ESSENTIAL', label: 'Essential' },
  { value: 'PLUS', label: 'Plus' },
];

const BulkVisibilityAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<GradeFilter>('ALL');
  // Per-formula transient save state for the "Saved" indicator.
  const [savedIds, setSavedIds] = useState<Record<number, boolean>>({});
  const [savingIds, setSavingIds] = useState<Record<number, boolean>>({});

  // Debounce timers keyed by formula id.
  const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const loadFormulas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/feed-formulas/bulk-visibility/');
      setFormulas(res.data || []);
    } catch (err) {
      console.error('Failed to load formulas:', err);
      setError('Failed to load formulas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormulas();
  }, [loadFormulas]);

  // Clean up any pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const persist = useCallback(async (id: number, value: boolean) => {
    setSavingIds(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/admin/feed-formulas/${id}/bulk-visibility/`, {
        available_bridge_club_bulk: value,
      });
      setSavingIds(prev => ({ ...prev, [id]: false }));
      setSavedIds(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setSavedIds(prev => ({ ...prev, [id]: false }));
      }, 1500);
    } catch (err) {
      console.error('Failed to save visibility:', err);
      setSavingIds(prev => ({ ...prev, [id]: false }));
      setError('Failed to save — reverting');
      // Revert optimistic change on failure.
      setFormulas(prev =>
        prev.map(f => (f.id === id ? { ...f, available_bridge_club_bulk: !value } : f))
      );
    }
  }, []);

  const toggle = useCallback((id: number, value: boolean) => {
    // Optimistic update.
    setFormulas(prev =>
      prev.map(f => (f.id === id ? { ...f, available_bridge_club_bulk: value } : f))
    );
    // Debounce the save 500ms.
    if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
    timersRef.current[id] = setTimeout(() => persist(id, value), 500);
  }, [persist]);

  const bulkSet = useCallback(async (predicate: (f: Formula) => boolean, value: boolean) => {
    const targets = formulas.filter(predicate);
    if (targets.length === 0) return;
    // Optimistic update for all targets.
    const ids = new Set(targets.map(t => t.id));
    setFormulas(prev =>
      prev.map(f => (ids.has(f.id) ? { ...f, available_bridge_club_bulk: value } : f))
    );
    // Persist sequentially (45 formulas — fine).
    for (const t of targets) {
      // eslint-disable-next-line no-await-in-loop
      await persist(t.id, value);
    }
  }, [formulas, persist]);

  const visible = formulas.filter(f => filter === 'ALL' || f.feed_grade === filter);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Bridge Club Bulk-Order Visibility</h1>
          <p style={styles.subtitle}>
            Toggle which formulas appear in the BC bulk catalog. Custom
            customer formulas should be off. Formulas without bulk pricing
            never appear, regardless of this flag.
          </p>
        </div>
        <button style={styles.backLink} onClick={() => navigate('/admin/pos/products')}>
          &larr; Back to POS Products
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.controls}>
        <div style={styles.filters}>
          {GRADE_FILTERS.map(g => (
            <button
              key={g.value}
              style={filter === g.value ? styles.filterActive : styles.filter}
              onClick={() => setFilter(g.value)}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div style={styles.bulkActions}>
          <button
            style={styles.bulkBtn}
            onClick={() => bulkSet(f => !f.is_custom_formula, true)}
          >
            Enable Standard Catalog
          </button>
          <button
            style={styles.bulkBtnSecondary}
            onClick={() => bulkSet(f => f.is_custom_formula, false)}
          >
            Disable All Custom Formulas
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading formulas…</div>
      ) : visible.length === 0 ? (
        <div style={styles.empty}>No formulas match this filter.</div>
      ) : (
        <div style={styles.list}>
          {visible.map(f => (
            <div key={f.id} style={styles.row}>
              <div style={styles.rowInfo}>
                <span style={styles.rowName}>{f.name}</span>
                <span style={styles.rowMeta}>
                  {f.feed_grade_display} · {f.animal_type_display}
                </span>
                {f.is_custom_formula && <span style={styles.customBadge}>Custom</span>}
                {!f.has_bulk_pricing && (
                  <span style={styles.noPriceBadge} title="No bulk pricing set — will not appear in catalog">
                    No bulk price
                  </span>
                )}
              </div>
              <div style={styles.rowControls}>
                {savingIds[f.id] && <span style={styles.savingText}>Saving…</span>}
                {savedIds[f.id] && <span style={styles.savedText}>Saved ✓</span>}
                <label style={styles.switch} title="Toggle bulk-order visibility">
                  <input
                    type="checkbox"
                    checked={f.available_bridge_club_bulk}
                    onChange={e => toggle(f.id, e.target.checked)}
                  />
                  <span style={f.available_bridge_club_bulk ? styles.onLabel : styles.offLabel}>
                    {f.available_bridge_club_bulk ? 'On' : 'Off'}
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 900, margin: '0 auto', padding: '24px 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 },
  subtitle: { fontSize: 14, color: '#6b7280', marginTop: 6, maxWidth: 560 },
  backLink: { background: 'none', border: 'none', color: '#059669', cursor: 'pointer', fontSize: 14, padding: '8px 0' },
  error: { background: '#fdecea', color: '#c62828', padding: '10px 14px', borderRadius: 8, margin: '12px 0' },
  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', margin: '20px 0 12px' },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filter: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', minHeight: 40 },
  filterActive: { padding: '8px 14px', borderRadius: 8, border: '1px solid #059669', background: '#059669', color: '#fff', cursor: 'pointer', minHeight: 40 },
  bulkActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  bulkBtn: { padding: '8px 14px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontWeight: 600, minHeight: 40 },
  bulkBtnSecondary: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', minHeight: 40 },
  loading: { padding: 40, textAlign: 'center', color: '#6b7280' },
  empty: { padding: 40, textAlign: 'center', color: '#9ca3af' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 },
  rowInfo: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  rowName: { fontWeight: 600, color: '#111827' },
  rowMeta: { fontSize: 12, color: '#6b7280' },
  customBadge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: '#fef3c7', color: '#92400e' },
  noPriceBadge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', color: '#9ca3af' },
  rowControls: { display: 'flex', alignItems: 'center', gap: 10 },
  savingText: { fontSize: 12, color: '#9ca3af' },
  savedText: { fontSize: 12, color: '#059669', fontWeight: 600 },
  switch: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minHeight: 40 },
  onLabel: { fontSize: 13, fontWeight: 600, color: '#059669' },
  offLabel: { fontSize: 13, fontWeight: 600, color: '#9ca3af' },
};

export default BulkVisibilityAdmin;
